import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { MAINTENANCE_MESSAGE, isMaintenanceStatus, toFriendlyErrorMessage } from "../../../utils/network";

const serverUrl = process.env.REACT_APP_SERVER_HOST;
const TOKEN_KEY = "auth_token";

const buildHeaders = () => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  return { Authorization: `Bearer ${token}` };
};

const ResultsPage = () => {
  const { testId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const result = state?.result;
  const [resultsData, setResultsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!result) {
      navigate("/dashboard");
      return;
    }

    const fetchResults = async () => {
      const headers = buildHeaders();
      if (!headers) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${serverUrl}/app/tests/${testId}/results`, { headers });

        if (response.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          navigate("/login");
          return;
        }

        if (isMaintenanceStatus(response.status)) {
          throw new Error(MAINTENANCE_MESSAGE);
        }

        if (!response.ok) {
          throw new Error(`Failed to load results (${response.status})`);
        }

        const data = await response.json();
        if (!Array.isArray(data)) {
          throw new Error("Unexpected results response from server");
        }

        // Validate each item has the expected structure
        const validatedData = data.filter(item =>
          typeof item === 'object' &&
          item !== null &&
          typeof item.questionId === 'number' &&
          typeof item.questionText === 'string' &&
          typeof item.answerText === 'string' &&
          typeof item.correctAnswer === 'string' &&
          typeof item.isCorrect === 'boolean'
        );

        if (validatedData.length !== data.length) {
          console.warn('Some results items were filtered out due to invalid structure');
        }

        setResultsData(validatedData);
      } catch (err) {
        setError(toFriendlyErrorMessage(err, "Unable to load results right now. Please try again."));
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [navigate, testId, result]);

  if (!result) {
    return null;
  }

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Arial, sans-serif",
          color: "#666",
        }}
      >
        Loading results...
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: "0 16px 60px",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          background: "#fff",
          borderBottom: "1px solid #e5e7eb",
          zIndex: 10,
          padding: "16px 0",
          marginBottom: "26px",
        }}
      >
        <div
          style={{
            maxWidth: "760px",
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
         <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#9ca3af",
              border: "none",
              padding: "7px 16px",
              borderRadius: "6px",
              cursor: "pointer",
              color: "#fff",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            {'<-'} Back to Tests
          </button>
          <span
            style={{
              background: "#1a56db",
              color: "#fff",
              padding: "3px 12px",
              borderRadius: "999px",
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "capitalize",
              letterSpacing: "0.5px",
            }}
          >
            Results
          </span>
        </div>
      </div>

      <div style={{ maxWidth: "760px", margin: "0 auto" }}>
        <h2 style={{ fontSize: "24px", fontWeight: 800, color: "#111827", marginBottom: "20px" }}>
          Detailed Results for Test {testId}
        </h2>

        {/* Test Metadata Section */}
        {result && (
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <h3 style={{ margin: "0 0 16px", color: "#1f2937", fontSize: "18px", fontWeight: 700 }}>
              Test Summary
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
              {result.testName && (
                <div>
                  <strong style={{ color: "#374151" }}>Test Name:</strong>{" "}
                  <span style={{ color: "#1f2937" }}>{result.testName}</span>
                </div>
              )}
              {result.attemptNumber && (
                <div>
                  <strong style={{ color: "#374151" }}>Attempt:</strong>{" "}
                  <span style={{ color: "#1f2937" }}>#{result.attemptNumber}</span>
                </div>
              )}
              {result.durationMinutes && (
                <div>
                  <strong style={{ color: "#374151" }}>Duration:</strong>{" "}
                  <span style={{ color: "#1f2937" }}>{result.durationMinutes} minutes</span>
                </div>
              )}
              {result.startedAt && (
                <div>
                  <strong style={{ color: "#374151" }}>Started:</strong>{" "}
                  <span style={{ color: "#1f2937" }}>
                    {new Date(result.startedAt).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              )}
              {result.submittedAt && (
                <div>
                  <strong style={{ color: "#374151" }}>Submitted:</strong>{" "}
                  <span style={{ color: "#1f2937" }}>
                    {new Date(result.submittedAt).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              )}
              {result.scorePercentage !== undefined && (
                <div>
                  <strong style={{ color: "#374151" }}>Score:</strong>{" "}
                  <span style={{
                    color: result.scorePercentage >= 60 ? "#16a34a" : "#dc3545",
                    fontWeight: 600
                  }}>
                    {result.scorePercentage.toFixed(1)}%
                  </span>
                </div>
              )}
              {result.message && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <strong style={{ color: "#374151" }}>Result:</strong>{" "}
                  <span style={{
                    color: result.message.toLowerCase().includes('pass') || result.message.toLowerCase().includes('excellent') ? "#16a34a" : "#dc3545",
                    fontWeight: 600
                  }}>
                    {result.message}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              padding: "14px 18px",
              background: "#fee2e2",
              color: "#991b1b",
              borderRadius: "8px",
              marginBottom: "20px",
              border: "1px solid #fca5a5",
            }}
          >
            {error}
          </div>
        )}

        {/* Statistics Summary Section */}
        {result && resultsData.length > 0 && (
          <div
            style={{
              background: "#ffffff",
              border: "2px solid #1a56db",
              borderRadius: "12px",
              padding: "24px",
              marginBottom: "28px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "16px",
            }}
          >
            {(() => {
              const totalQuestions = result.totalQuestions || resultsData.length;
              const answeredQuestions = result.answeredQuestions || resultsData.filter(
                (q) => q.answerText && q.answerText.trim()
              ).length;
              const correctQCount = result.correctAnswers || resultsData.filter((q) => q.isCorrect).length;
              const incorrectAnswers = answeredQuestions - correctQCount;

              return (
                <>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: 800,
                        color: "#1a56db",
                        marginBottom: "8px",
                      }}
                    >
                      {totalQuestions}
                    </div>
                    <div style={{ fontSize: "14px", color: "#6b7280", fontWeight: 600 }}>
                      Total Questions
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: 800,
                        color: "#3b82f6",
                        marginBottom: "8px",
                      }}
                    >
                      {answeredQuestions}
                    </div>
                    <div style={{ fontSize: "14px", color: "#6b7280", fontWeight: 600 }}>
                      Answered
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: 800,
                        color: "#16a34a",
                        marginBottom: "8px",
                      }}
                    >
                      {correctQCount}
                    </div>
                    <div style={{ fontSize: "14px", color: "#6b7280", fontWeight: 600 }}>
                      Correct
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: 800,
                        color: "#dc3545",
                        marginBottom: "8px",
                      }}
                    >
                      {incorrectAnswers}
                    </div>
                    <div style={{ fontSize: "14px", color: "#6b7280", fontWeight: 600 }}>
                      Incorrect
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {resultsData.map((question, index) => {
          const isCorrect = question.isCorrect;

          return (
            <div
              key={question.questionId}
              style={{
                background: "#ffffff",
                border: "1px solid #dbe4f0",
                borderRadius: "12px",
                padding: "18px 20px",
                marginBottom: "16px",
              }}
            >
              <div style={{ fontWeight: 700, color: "#1f2937", marginBottom: "10px" }}>
                {index + 1}. {question.questionText}
              </div>
              <div style={{ marginBottom: "10px" }}>
                <strong>Your Answer:</strong>{" "}
                <span style={{ color: isCorrect ? "#16a34a" : "#dc3545" }}>
                  {question.answerText || "Not answered"}
                </span>
              </div>
              {!isCorrect && question.correctAnswer && (
                <div>
                  <strong>Correct Answer:</strong>{" "}
                  <span style={{ color: "#16a34a" }}>{question.correctAnswer}</span>
                </div>
              )}
            </div>
          );
        })}

        <div style={{ textAlign: "center", marginTop: "30px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              padding: "12px 24px",
              background: "#28a745",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
